import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile, PR, EvolveTier, CoachingStyle, TrialStatus } from '@/types/evolve';
import { toast } from 'sonner';

interface DatabaseProfile {
  id: string;
  name: string | null;
  coaching_style: string | null;
  days_active: number | null;
  current_tier: string | null;
  prs: any;
  trial_started_at: string | null;
  is_premium: boolean | null;
  onboarding_complete: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  age: number | null;
  weight: number | null;
  goal: string | null;
  experience: string | null;
  motivation: string | null;
  sex: string | null;
  height: string | null;
  days_per_week: number | null;
  equipment: string | null;
  injuries: string | null;
  diet_preferences: string | null;
  specific_goal: string | null;
  avatar_url: string | null;
  referral_code: string | null;
  is_evolver_founder: boolean | null;
  premium_days_bonus: number | null;
  show_micronutrients: boolean | null;
  activity_level: string | null;
  preferred_training_time: string | null;
  display_name: string | null;
  leaderboard_privacy: string | null;
  weight_unit: string | null;
  favorite_exercises: string[] | null;
  // Health sync preferences
  health_sync_enabled: boolean | null;
  health_sync_weight: boolean | null;
  health_sync_workouts: boolean | null;
  health_sync_steps: boolean | null;
  // Nutrition targets
  daily_calorie_target: number | null;
  protein_target_g: number | null;
  carbs_target_g: number | null;
  fat_target_g: number | null;
  nutrition_goal_type: string | null;
  // Tour and celebration tracking
  has_seen_tour: boolean | null;
  last_celebrated_tier: string | null;
  // Notification preferences
  notification_preferences: {
    training_reminders?: boolean;
    weigh_in_nudges?: boolean;
    motivational_messages?: boolean;
    progress_updates?: boolean;
    push_enabled?: boolean;
  } | null;
}

// User stats from onboarding
export interface UserStats {
  age?: number;
  sex?: string;
  height?: string;
  weight?: number;
  goalWeight?: number;
  goal?: string;
  specificGoal?: string;
  experience?: string;
  daysPerWeek?: number;
  minutesPerWorkout?: number;
  preferredTrainingTime?: string;
  equipment?: string;
  injuries?: string;
  dietPreferences?: string;
}

export type NutritionGoalType = 'lose' | 'maintain' | 'gain';

export interface NutritionTargets {
  calories?: number;
  protein?: number;
  carbs?: number;
  fat?: number;
  goalType?: NutritionGoalType;
}

// Extended profile that includes PRs (stored in DB but not in UserProfile type)
export interface ExtendedProfile extends Omit<UserProfile, 'id' | 'joinDate' | 'totalWorkouts' | 'trialStatus' | 'hasCompletedOnboarding'> {
  prs: PR[];
  trialStartedAt: string | null;
  createdAt: string | null;
  stats?: UserStats;
  avatarUrl?: string;
  referralCode?: string;
  isEvolverFounder?: boolean;
  premiumDaysBonus?: number;
  showMicronutrients?: boolean;
  activityLevel?: string;
  displayName?: string;
  leaderboardPrivacy?: 'full_name' | 'nickname' | 'anonymous' | 'hidden';
  weightUnit?: 'lbs' | 'kg';
  favoriteExercises?: string[];
  // Health sync preferences
  healthSyncEnabled?: boolean;
  healthSyncWeight?: boolean;
  healthSyncWorkouts?: boolean;
  healthSyncSteps?: boolean;
  // Nutrition targets
  nutritionTargets?: NutritionTargets;
  // Tour and celebration tracking
  hasSeenTour?: boolean;
  lastCelebratedTier?: string;
  // Notification preferences
  notificationPreferences?: {
    trainingReminders?: boolean;
    weighInNudges?: boolean;
    motivationalMessages?: boolean;
    progressUpdates?: boolean;
    pushEnabled?: boolean;
  };
}

const defaultProfile: ExtendedProfile = {
  name: '',
  coachingStyle: 'chill-veteran',
  daysActive: 0,
  tier: 'Seed',
  prs: [],
  trialStartedAt: null,
  createdAt: null,
  isPremium: false,
  stats: undefined,
  referralCode: undefined,
  isEvolverFounder: false,
  premiumDaysBonus: 0,
  showMicronutrients: false,
  activityLevel: 'moderate',
  displayName: undefined,
  leaderboardPrivacy: 'full_name',
  weightUnit: 'lbs',
  favoriteExercises: [],
  healthSyncEnabled: false,
  healthSyncWeight: true,
  healthSyncWorkouts: true,
  healthSyncSteps: false,
  nutritionTargets: undefined,
  hasSeenTour: false,
  lastCelebratedTier: undefined,
  notificationPreferences: {
    trainingReminders: true,
    weighInNudges: true,
    motivationalMessages: true,
    progressUpdates: true,
    pushEnabled: false,
  },
};

export const useProfile = (userId: string | undefined) => {
  const [profile, setProfile] = useState<ExtendedProfile>(defaultProfile);
  const [loading, setLoading] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset loading when userId changes (e.g., after login)
  useEffect(() => {
    if (userId) {
      setLoading(true);
      setError(null);
    }
  }, [userId]);

  const fetchProfile = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        const dbProfile = data as DatabaseProfile;
        // Map old coaching styles to new ones
        let coachingStyle: CoachingStyle = 'drill-sergeant';
        const dbStyle = dbProfile.coaching_style;
        if (dbStyle === 'tough' || dbStyle === 'drill-sergeant') {
          coachingStyle = 'drill-sergeant';
        } else if (dbStyle === 'soft' || dbStyle === 'tough-love') {
          coachingStyle = 'tough-love';
        } else if (dbStyle === 'chill-veteran') {
          coachingStyle = 'chill-veteran';
        } else if (dbStyle === 'hype-motivator') {
          coachingStyle = 'hype-motivator';
        }
        
        setProfile({
          name: dbProfile.name || '',
          coachingStyle,
          daysActive: dbProfile.days_active || 0,
          tier: (dbProfile.current_tier as EvolveTier) || 'Seed',
          prs: Array.isArray(dbProfile.prs) ? dbProfile.prs : [],
          trialStartedAt: dbProfile.trial_started_at || null,
          createdAt: dbProfile.created_at || null,
          isPremium: dbProfile.is_premium || false,
          motivation: dbProfile.motivation || undefined,
          avatarUrl: dbProfile.avatar_url || undefined,
          referralCode: dbProfile.referral_code || undefined,
          isEvolverFounder: dbProfile.is_evolver_founder || false,
          premiumDaysBonus: dbProfile.premium_days_bonus || 0,
          showMicronutrients: dbProfile.show_micronutrients || false,
          activityLevel: dbProfile.activity_level || 'moderate',
          displayName: dbProfile.display_name || undefined,
          leaderboardPrivacy: (dbProfile.leaderboard_privacy as 'full_name' | 'nickname' | 'anonymous' | 'hidden') || 'full_name',
          weightUnit: (dbProfile.weight_unit as 'lbs' | 'kg') || 'lbs',
          favoriteExercises: Array.isArray(dbProfile.favorite_exercises) ? dbProfile.favorite_exercises : [],
          healthSyncEnabled: dbProfile.health_sync_enabled ?? false,
          healthSyncWeight: dbProfile.health_sync_weight ?? true,
          healthSyncWorkouts: dbProfile.health_sync_workouts ?? true,
          healthSyncSteps: dbProfile.health_sync_steps ?? false,
          nutritionTargets: {
            calories: dbProfile.daily_calorie_target || undefined,
            protein: dbProfile.protein_target_g || undefined,
            carbs: dbProfile.carbs_target_g || undefined,
            fat: dbProfile.fat_target_g || undefined,
            goalType: (dbProfile.nutrition_goal_type as NutritionGoalType) || undefined,
          },
          stats: {
            age: dbProfile.age || undefined,
            sex: dbProfile.sex || undefined,
            height: dbProfile.height || undefined,
            weight: dbProfile.weight || undefined,
            goal: dbProfile.goal || undefined,
            specificGoal: dbProfile.specific_goal || undefined,
            experience: dbProfile.experience || undefined,
            daysPerWeek: dbProfile.days_per_week || undefined,
            preferredTrainingTime: dbProfile.preferred_training_time || undefined,
            equipment: dbProfile.equipment || undefined,
            injuries: dbProfile.injuries || undefined,
            dietPreferences: dbProfile.diet_preferences || undefined,
          },
          hasSeenTour: dbProfile.has_seen_tour ?? false,
          lastCelebratedTier: dbProfile.last_celebrated_tier || undefined,
          notificationPreferences: dbProfile.notification_preferences ? {
            trainingReminders: dbProfile.notification_preferences.training_reminders ?? true,
            weighInNudges: dbProfile.notification_preferences.weigh_in_nudges ?? true,
            motivationalMessages: dbProfile.notification_preferences.motivational_messages ?? true,
            progressUpdates: dbProfile.notification_preferences.progress_updates ?? true,
            pushEnabled: dbProfile.notification_preferences.push_enabled ?? false,
          } : undefined,
        });
        setOnboardingComplete(dbProfile.onboarding_complete || false);
      }
    } catch (err: any) {
      console.error('Error fetching profile:', err);
      setError(err.message || 'Failed to load profile');
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const updateProfile = useCallback(async (updates: Partial<ExtendedProfile> & { onboardingComplete?: boolean }) => {
    if (!userId) return;

    const dbUpdates: Record<string, any> = {};
    
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.coachingStyle !== undefined) dbUpdates.coaching_style = updates.coachingStyle;
    if (updates.daysActive !== undefined) dbUpdates.days_active = updates.daysActive;
    if (updates.tier !== undefined) dbUpdates.current_tier = updates.tier;
    if (updates.prs !== undefined) dbUpdates.prs = updates.prs;
    if (updates.trialStartedAt !== undefined) dbUpdates.trial_started_at = updates.trialStartedAt;
    // NOTE: isPremium is protected by database trigger - cannot be set from client
    // Only the check-subscription edge function can set is_premium to true
    if (updates.onboardingComplete !== undefined) dbUpdates.onboarding_complete = updates.onboardingComplete;
    if (updates.motivation !== undefined) dbUpdates.motivation = updates.motivation;
    if (updates.avatarUrl !== undefined) dbUpdates.avatar_url = updates.avatarUrl;
    if (updates.showMicronutrients !== undefined) dbUpdates.show_micronutrients = updates.showMicronutrients;
    if (updates.activityLevel !== undefined) dbUpdates.activity_level = updates.activityLevel;
    if (updates.displayName !== undefined) dbUpdates.display_name = updates.displayName;
    if (updates.leaderboardPrivacy !== undefined) dbUpdates.leaderboard_privacy = updates.leaderboardPrivacy;
    if (updates.weightUnit !== undefined) dbUpdates.weight_unit = updates.weightUnit;
    if (updates.favoriteExercises !== undefined) dbUpdates.favorite_exercises = updates.favoriteExercises;
    if (updates.nutritionTargets !== undefined) {
      if (updates.nutritionTargets.calories !== undefined) dbUpdates.daily_calorie_target = updates.nutritionTargets.calories;
      if (updates.nutritionTargets.protein !== undefined) dbUpdates.protein_target_g = updates.nutritionTargets.protein;
      if (updates.nutritionTargets.carbs !== undefined) dbUpdates.carbs_target_g = updates.nutritionTargets.carbs;
      if (updates.nutritionTargets.fat !== undefined) dbUpdates.fat_target_g = updates.nutritionTargets.fat;
      if (updates.nutritionTargets.goalType !== undefined) dbUpdates.nutrition_goal_type = updates.nutritionTargets.goalType;
    }
    if (updates.stats !== undefined) {
      if (updates.stats.age !== undefined) dbUpdates.age = updates.stats.age;
      if (updates.stats.sex !== undefined) dbUpdates.sex = updates.stats.sex;
      if (updates.stats.height !== undefined) dbUpdates.height = updates.stats.height;
      if (updates.stats.weight !== undefined) dbUpdates.weight = updates.stats.weight;
      if (updates.stats.goal !== undefined) dbUpdates.goal = updates.stats.goal;
      if (updates.stats.specificGoal !== undefined) dbUpdates.specific_goal = updates.stats.specificGoal;
      if (updates.stats.experience !== undefined) dbUpdates.experience = updates.stats.experience;
      if (updates.stats.daysPerWeek !== undefined) dbUpdates.days_per_week = updates.stats.daysPerWeek;
      if (updates.stats.preferredTrainingTime !== undefined) dbUpdates.preferred_training_time = updates.stats.preferredTrainingTime;
      if (updates.stats.equipment !== undefined) dbUpdates.equipment = updates.stats.equipment;
      if (updates.stats.injuries !== undefined) dbUpdates.injuries = updates.stats.injuries;
      if (updates.stats.dietPreferences !== undefined) dbUpdates.diet_preferences = updates.stats.dietPreferences;
    }
    // Health sync preferences
    if (updates.healthSyncEnabled !== undefined) dbUpdates.health_sync_enabled = updates.healthSyncEnabled;
    if (updates.healthSyncWeight !== undefined) dbUpdates.health_sync_weight = updates.healthSyncWeight;
    if (updates.healthSyncWorkouts !== undefined) dbUpdates.health_sync_workouts = updates.healthSyncWorkouts;
    if (updates.healthSyncSteps !== undefined) dbUpdates.health_sync_steps = updates.healthSyncSteps;
    // Tour and celebration tracking
    if (updates.hasSeenTour !== undefined) dbUpdates.has_seen_tour = updates.hasSeenTour;
    if (updates.lastCelebratedTier !== undefined) dbUpdates.last_celebrated_tier = updates.lastCelebratedTier;
    if (updates.notificationPreferences !== undefined) {
      dbUpdates.notification_preferences = {
        training_reminders: updates.notificationPreferences.trainingReminders,
        weigh_in_nudges: updates.notificationPreferences.weighInNudges,
        motivational_messages: updates.notificationPreferences.motivationalMessages,
        progress_updates: updates.notificationPreferences.progressUpdates,
        push_enabled: updates.notificationPreferences.pushEnabled,
      };
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update(dbUpdates)
        .eq('id', userId);

      if (error) throw error;

      setProfile(prev => ({
        ...prev,
        ...updates,
      }));
      
      if (updates.onboardingComplete !== undefined) {
        setOnboardingComplete(updates.onboardingComplete);
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast.error('Failed to save profile');
    }
  }, [userId]);

  return { profile, setProfile, loading, error, onboardingComplete, updateProfile, refetch: fetchProfile };
};
